import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

// Async function to fetch user details
const fetchUserDetails = async (userId) => {
  try {
    const response = await axiosClient.get(`setting/getProfileDt/${userId}`);
    if (response.data.statusCode === 200) {
      return response.data.profile;
    } else {
      throw new Error(response.data.msg);
    }
  } catch (error) {
    throw new Error("Error fetching userDetails");
  }
};

const fetchHeaderCounts = async (userId, roleType) => {
  try {
    const response = await axiosClient.get(
      `/setting/headerCount/${userId}?roleType=${roleType}`,
      {}
    );

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Error fetching header counts"
    );
  }
};

const fetchSidebarCounts = async (userId, roleType) => {
  try {
    const response = await axiosClient.get(
      `/setting/sidebarCount/${userId}?roleType=${roleType}`,
      {}
    );

    if (response.data.statusCode === 200) {
      return response.data;
    } else {
      throw new Error(
        response.data.message || "Failed to fetch sidebar counts"
      );
    }
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Error fetching sidebar counts"
    );
  }
};

// Async thunk to fetch user details
export const fetchUserDetailsThunk = createAsyncThunk(
  "user/fetchUserDetails",
  async (userId, { rejectWithValue }) => {
    try {
      const profile = await fetchUserDetails(userId);
      return profile;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch header counts
export const fetchHeaderCountsThunk = createAsyncThunk(
  "user/fetchHeaderCounts",
  async ({ userId, roleType }, { rejectWithValue }) => {
    try {
      const counts = await fetchHeaderCounts(userId, roleType);
      return counts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk to fetch sidebar counts
export const fetchSidebarCountsThunk = createAsyncThunk(
  "user/fetchSidebarCounts",
  async ({ userId, roleType }, { rejectWithValue }) => {
    try {
      const counts = await fetchSidebarCounts(userId, roleType);
      return counts;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Safely parse USER_DATA to prevent invalid JSON errors
const getUserInfo = () => {
  const userData = localStorage.getItem("USER_DATA");
  if (userData) {
    try {
      return userData; // Return as string to match original format
    } catch (e) {
      console.error("Failed to parse USER_DATA:", e);
      return "[]";
    }
  }
  return "[]";
};

const initialState = {
  isLogin: localStorage.getItem("isLogin") === "true" || false,
  token: localStorage.getItem("ACCESS_TOKEN") || null,
  userInfo: getUserInfo(),
  userDetails: {
    profileDetails: null,
    status: "idle",
    error: null,
    lastFetched: null,
  },
  // Header counts from API
  headerCounts: {
    cartCount: "0",
    messageCount: "0",
    notificationCount: "0",
    status: "idle",
    error: null,
    lastFetched: null,
  },
  // Sidebar counts from API
  sidebarCounts: {
    needCount: "0",
    serviceCount: "0",
    imageCount: "0",
    messageCount: "0",
    status: "idle",
    error: null,
    lastFetched: null,
  },
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    decrementMessageCount: (state) => {
      const currentCount = parseInt(state.headerCounts.messageCount || "0", 10);
      if (currentCount > 0) {
        state.headerCounts.messageCount = (currentCount - 1).toString();
      }
      // If already 0, stay at 0 — no negative values
    },
    setLoginInfo(state, action) {
      const info = action.payload;
      localStorage.setItem("USER_DATA", JSON.stringify(info.userInfo || []));
      localStorage.setItem("isLogin", "true");
      localStorage.setItem("ACCESS_TOKEN", info.token || "");
      state.userInfo = JSON.stringify(info.userInfo || []);
      state.token = info.token || null;
      state.isLogin = true;
      window.dispatchEvent(new Event("localStorageChange"));
    },
    logout(state) {
      localStorage.removeItem("ACCESS_TOKEN");
      localStorage.removeItem("USER_DATA");
      localStorage.removeItem("isLogin");
      localStorage.removeItem("credentials");
      state.token = null;
      state.userInfo = "[]";
      state.isLogin = false;
      // Reset userDetails
      state.userDetails = {
        profileDetails: null,
        status: "idle",
        error: null,
        lastFetched: null,
      };
      // Reset counts
      state.headerCounts = {
        cartCount: "0",
        messageCount: "0",
        notificationCount: "0",
        status: "idle",
        error: null,
        lastFetched: null,
      };
      state.sidebarCounts = {
        needCount: "0",
        serviceCount: "0",
        imageCount: "0",
        messageCount: "0",
        status: "idle",
        error: null,
        lastFetched: null,
      };
      window.dispatchEvent(new Event("localStorageChange"));
    },
    syncLocalStorage(state) {
      state.isLogin = localStorage.getItem("isLogin") === "true" || false;
      state.token = localStorage.getItem("ACCESS_TOKEN") || null;
      state.userInfo = getUserInfo();
    },
    resetUserDetails(state) {
      state.userDetails = {
        profileDetails: null,
        status: "idle",
        error: null,
        lastFetched: null,
      };
    },
    resetHeaderCounts(state) {
      state.headerCounts = {
        cartCount: "0",
        messageCount: "0",
        notificationCount: "0",
        status: "idle",
        error: null,
        lastFetched: null,
      };
    },
    resetSidebarCounts(state) {
      state.sidebarCounts = {
        needCount: "0",
        serviceCount: "0",
        imageCount: "0",
        messageCount: "0",
        status: "idle",
        error: null,
        lastFetched: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // User Details
      .addCase(fetchUserDetailsThunk.pending, (state) => {
        state.userDetails.status = "loading";
      })
      .addCase(fetchUserDetailsThunk.fulfilled, (state, action) => {
        state.userDetails.status = "succeeded";
        state.userDetails.profileDetails = action.payload;
        state.userDetails.error = null;
        state.userDetails.lastFetched = Date.now();
      })
      .addCase(fetchUserDetailsThunk.rejected, (state, action) => {
        state.userDetails.status = "failed";
        state.userDetails.error = action.payload;
      })

      // Header Counts
      .addCase(fetchHeaderCountsThunk.pending, (state) => {
        state.headerCounts.status = "loading";
      })
      .addCase(fetchHeaderCountsThunk.fulfilled, (state, action) => {
        const data = action.payload;
        state.headerCounts.status = "succeeded";
        state.headerCounts.cartCount = data.cartCount ?? "0";
        state.headerCounts.messageCount = data.messageCount ?? "0";
        state.headerCounts.notificationCount = data.notificationCount ?? "0";
        state.headerCounts.error = null;
        state.headerCounts.lastFetched = Date.now();
      })
      .addCase(fetchHeaderCountsThunk.rejected, (state, action) => {
        state.headerCounts.status = "failed";
        state.headerCounts.error = action.payload;
      })

      // Sidebar Counts
      .addCase(fetchSidebarCountsThunk.pending, (state) => {
        state.sidebarCounts.status = "loading";
      })
      .addCase(fetchSidebarCountsThunk.fulfilled, (state, action) => {
        const data = action.payload;
        state.sidebarCounts.status = "succeeded";
        state.sidebarCounts.needCount = data.needCount ?? "0";
        state.sidebarCounts.serviceCount = data.serviceCount ?? "0";
        state.sidebarCounts.imageCount = data.imageCount ?? "0";
        state.sidebarCounts.messageCount = data.messageCount ?? "0";
        state.sidebarCounts.caidCount = data.caidCount ?? "0";
        state.sidebarCounts.error = null;
        state.sidebarCounts.lastFetched = Date.now();
      })
      .addCase(fetchSidebarCountsThunk.rejected, (state, action) => {
        state.sidebarCounts.status = "failed";
        state.sidebarCounts.error = action.payload;
      });
  },
});

// Initialize listener for localStorage changes with debounce
export const initLocalStorageSync = (store) => {
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };
  const debouncedSync = debounce(() => {
    store.dispatch(userAction.syncLocalStorage());
  }, 100);
  window.addEventListener("localStorageChange", debouncedSync);
  window.addEventListener("storage", (event) => {
    if (
      event.key === "ACCESS_TOKEN" ||
      event.key === "USER_DATA" ||
      event.key === "isLogin"
    ) {
      debouncedSync();
    }
  });
};

// Utility to update localStorage safely
export const updateLocalStorage = (key, value) => {
  if (key === "USER_DATA") {
    localStorage.setItem(key, JSON.stringify(value || []));
  } else {
    localStorage.setItem(key, value || "");
  }
  window.dispatchEvent(new Event("localStorageChange"));
};
export const { decrementMessageCount } = userSlice.actions;

export const userAction = userSlice.actions;
export default userSlice.reducer;
